// AWS Secrets Manager's rule for a secret's name. It is the looser of the two
// vaults' rules: every name Google Secret Manager takes (letters, digits,
// underscores and hyphens, up to 255 characters) fits it too. It admits no
// whitespace, colon, quote or wildcard, which keeps out an ARN, a wildcard
// and the admin credential's JSON.
const SECRET_NAME_PATTERN = /^[A-Za-z0-9/_+=.@-]{1,512}$/;

/** What to enter when {@link isSecretName} refuses a value. */
export const INVALID_SECRET_NAME =
  "That doesn't look like a secret name. Enter the name of the secret that holds the Harbor admin credential, not its ARN or the credential itself.";

/**
 * Whether `name`, ignoring surrounding whitespace, has the shape of a secret's
 * name in at least one kind of vault a Harbor instance can use.
 *
 * It is all the field's own validator can check, since an element's validator
 * doesn't see the vault beside it. A name that passes may still not be one in
 * the vault the instance names.
 */
export const isSecretName = (name: unknown): name is string =>
  typeof name === "string" && SECRET_NAME_PATTERN.test(name.trim());

// TODO(INT-431): the per-vault rule below is a copy of github-repositories'
// `findSecretNameProblem`
// (packages/integrations/github-repositories/shared/src/secret-name.ts), with
// Google Secret Manager's rule beside it, kept close to it so that Harbor can
// take the shared one once it lands. Change both or neither.

// Google Secret Manager's rule for a secret's ID. It is also what the Harbor
// connector takes for one, so that a full resource name
// (`projects/<project>/secrets/<name>`) can't name a secret outside the
// vault's project.
const GCP_SECRET_NAME_PATTERN = /^[A-Za-z0-9_-]{1,255}$/;

// The hyphen and six letters or digits that Secrets Manager adds to a secret's
// name in its full ARN. It reads a partial ARN that ends this way as a full
// one, and finds no secret by it.
const AWS_ARN_SUFFIX = /-[A-Za-z0-9]{6}$/;

/** Refuses a name that Google Secret Manager wouldn't take, without repeating
 * it. */
export const INVALID_GCP_SECRET_NAME =
  "That isn't a secret's name in Google Secret Manager. Enter the name alone, without its project or a version: letters, digits, underscores and hyphens, up to 255 characters.";

/** Refuses a name that Secrets Manager would read as ending in a full ARN's
 * suffix. */
export const SUFFIXED_SECRET_NAME =
  "Secrets Manager can't find a secret in another account by its name when the name ends in a hyphen and six characters, like -AbCdEf. Use a secret whose name doesn't end that way, or one in the connector's own account.";

/** The vault a secret is in and, on AWS, how the connector is pointed at it:
 * by its `name` when the vault is in the connector's own account, and by its
 * `partial-arn` when it's in another, which a name alone can't reach. */
export type SecretLookup =
  | { vault: "aws-sm"; by: "name" | "partial-arn" }
  | { vault: "gcp-sm" };

/**
 * Why `name` can't name the admin credential's secret for `lookup`, or
 * `undefined` if it can.
 *
 * The name is checked as it stands, so one with whitespace around it is
 * refused, where {@link isSecretName} ignores it: this runs on a new
 * instance's request, before the install framework trims anything, and what
 * is typed is what the instance's Terraform is rendered with.
 *
 * Each problem is a fixed message: none repeats `name`, which may be the
 * credentials pasted in its place.
 */
export const findSecretNameProblem = (
  name: unknown,
  lookup: SecretLookup
): string | undefined => {
  const candidate = typeof name === "string" ? name : undefined;
  if (lookup.vault === "gcp-sm") {
    return candidate !== undefined && GCP_SECRET_NAME_PATTERN.test(candidate)
      ? undefined
      : INVALID_GCP_SECRET_NAME;
  }
  if (candidate === undefined || !SECRET_NAME_PATTERN.test(candidate)) {
    return INVALID_SECRET_NAME;
  }
  return lookup.by === "partial-arn" && AWS_ARN_SUFFIX.test(candidate)
    ? SUFFIXED_SECRET_NAME
    : undefined;
};
